import React from 'react';
import { money, listingLabel, detailLines } from './LaMatchResults';
import { AsOf } from './LaListingsTable';
import RetryCountdown from '../RetryCountdown';
import { applicableRungs, floorSummary, pctText, unpricedRungs, unresolvedText } from '../../utils/lionAutoLadder';
import { floorSourceText } from '../../utils/lionAutoFloors';
import { holdHeadText, holdWarning } from '../../utils/lionAutoHold';
import { ANCHOR_OUTLIER_TITLE, anchorOutlierText } from '../../utils/lionAutoOutliers';
import { anchorCompLabel } from '../../utils/lionAutoListings';
import { newPricesLabel, noCompBanner, noCompHeadText } from '../../utils/lionAutoNoComp';

// The price ladder of the group on screen, above the table: how it is set
// (rank 1 at $offset below the anchor comp, +$step per rank, both in L, the
// floor: no rank under its own cost × the %, and holding when on: the ranks
// after the live ones held at one hold price, a B, with a warning when that
// price would undercut the live ones), Calculate prices (the server finds
// each rank's B through Skybox's what-if; once the run is complete the page
// writes the new Bs to Skybox, with no review step — it needs a floor) and,
// once calculated, the anchor comp it hangs from, how many listings the
// floor held (held ranks priced at a floor above the hold price counted
// apart), what stopped it and Continue (a run that stopped early picks up
// where it stopped; nothing is written until it is complete). With no anchor
// comp and the no-comp step-down on (lionAutoNoComp) the answer is the Auto
// runner's step-down, previewed: a banner says what Auto does (or would do)
// and Calculate writes none of it. The last
// write's outcome stays here as a banner: refused rows, a stop, a 429's
// countdown, or a timed-out write that may still be applying. The rows'
// New B / New L, and which ones the floor held, are in the table.

const dollars = (v) => `$${(Number(v) || 0).toFixed(2)}`;
// Rows listed by name in a banner (refused, unpriced), then a count
const MAX_LISTED = 10;
const pricesText = (n) => `${n} price${n === 1 ? '' : 's'}`;

// The page's error for a Continue whose rungs no longer line up with the
// answer on screen (a listing sold or bought in between), or that was
// priced at another floor (mergeLadder refused it)
export const LADDER_CHANGED = { ladderChanged: true };

// A failed Calculate / Continue in plain words (a 429 is the page's toast)
function describeLadderError(err) {
  if (err?.ladderChanged) {
    return { text: "The group's listings (a sale or a purchase) or its floor changed since Calculate, so the rest can't be added on — Calculate again." };
  }
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 404) {
    // A bare { error: 'Not found' } (no code) is the server's unknown-route
    // answer: the ladder backend is not deployed there yet
    if (data.code === 'PRICING_NOT_FOUND') return { text: 'Skybox has no pricing for this event.' };
    return { text: data.code && data.error ? data.error : 'Price ladder is not available on this server yet.' };
  }
  if (status === 403) return { text: 'You do not have access to this event.' };
  if (status === 400) return { text: data.error || 'The ladder settings were not accepted.', details: detailLines(data.details) };
  if (!status || err?.code === 'ECONNABORTED' || status === 502 || status === 503 || status === 504) {
    return { text: 'Skybox did not answer in time — try again in a moment.', retryable: true };
  }
  return { text: data.error || 'Calculating the ladder failed.', retryable: true };
}

// The answer's status codes, plus what it skipped, as banners. warn = the
// ladder is missing rows (or all of them): each such cause says nothing was
// written (Calculate writes only a complete ladder: ladderEndAction).
// canContinue: a stopped run can pick up where it stopped. details: lines
// listed under the text. noCompCtx: noCompBanner's context ({ group,
// dryRun, zoneFallback, now }); with the step-down on its banner replaces the
// plain no-anchor-comp one.
function ladderBanners(data, canContinue, autoContinuing = false, noCompCtx = {}) {
  const codes = new Set(data.status || []);
  const rungs = Array.isArray(data.rungs) ? data.rungs : [];
  const out = [];
  if (codes.has('NO_SECTIONS')) out.push({ code: 'NO_SECTIONS', kind: 'warn', text: 'This group has no sections yet, so there is nothing to price and nothing was written.' });
  const stepDown = noCompBanner(data, noCompCtx);
  if (stepDown) {
    out.push(stepDown);
  } else if (codes.has('NO_ANCHOR_COMP')) {
    // Holding on: the held ranks need no comp, so they still have a price
    const held = rungs.some(r => r?.tier === 'HELD');
    out.push({
      code: 'NO_ANCHOR_COMP', kind: 'warn',
      text: held
        ? 'No anchor comp: the live ranks have no price, so nothing was written (not even the held ranks at the hold price).'
        : 'No anchor comp: no market listing matches this group, so nothing was priced or written.',
    });
  }
  if (codes.has('NO_RANKED_MEMBERS')) {
    out.push({ code: 'NO_RANKED_MEMBERS', kind: 'warn', text: 'None of the ranked listings is in the group now (sold, or kept out by its filters), so nothing was written. Rank them again.' });
  }
  if (!rungs.length && !codes.has('NO_SECTIONS') && !codes.has('NO_ANCHOR_COMP') && !codes.has('NO_RANKED_MEMBERS')) {
    out.push({ code: 'EMPTY', kind: 'warn', text: 'The ladder came back with no ranks, so nothing was written.' });
  }
  if (codes.has('PARTIAL')) {
    out.push({
      code: 'PARTIAL', kind: 'warn',
      text: autoContinuing
        ? 'Skybox is slow on a long group: pricing the rest now…'
        : `Stopped early (Skybox took too long): some rows have no price yet, so nothing was written. ${canContinue ? 'Continue prices the rest, then writes.' : 'Calculate again to retry them.'}`,
    });
  }
  if (codes.has('WHATIF_FAILED')) {
    out.push({
      code: 'WHATIF_FAILED', kind: 'warn',
      text: "Skybox's what-if gave no answer for some rows (Skybox down, or your Skybox session expired): they have no price, so nothing was written. Calculate again in a moment.",
    });
  }
  // Ranks left without a price that no code above explains (a flat what-if,
  // no bracket, an unreachable target): each one stops the write
  const unpriced = unpricedRungs(data);
  if (unpriced.length) {
    const one = unpriced.length === 1;
    out.push({
      code: 'UNPRICED', kind: 'warn',
      text: `${unpriced.length} rank${one ? '' : 's'} got no price, so nothing was written (${one ? 'it' : 'they'} would keep the old B and could break the order). Change the ladder or Calculate again:`,
      details: [
        ...unpriced.slice(0, MAX_LISTED).map(r => `Rank ${r.rank} · ${listingLabel(r)}: ${unresolvedText(r.reason)}`),
        ...(unpriced.length > MAX_LISTED ? [`…and ${unpriced.length - MAX_LISTED} more`] : []),
      ],
    });
  }
  const dropped = Number(data.dropped) || 0;
  if (dropped > 0) {
    out.push({
      code: 'DROPPED', kind: '',
      text: `${dropped} ranked listing${dropped === 1 ? ' is' : 's are'} no longer in the group (sold, or kept out by its filters): skipped, the ranks after ${dropped === 1 ? 'it' : 'them'} closed up.`,
    });
  }
  const unranked = (data.unranked || []).length;
  if (unranked > 0) {
    const one = unranked === 1;
    out.push({ code: 'UNRANKED', kind: '', text: `${unranked} of your listings in the group ${one ? 'is' : 'are'} not ranked and keep${one ? 's its' : ' their'} price.` });
  }
  return out;
}

// A refused row as the pricer names it
const refusedLine = (f) => {
  const where = f.rung ? `Rank ${f.rung.rank} · ${listingLabel(f.rung)}` : `Listing ${f.inventoryId}`;
  return `${where}: ${f.error}`;
};

// The last write's outcome (useLadderWrite), sticky until the next Calculate
function WriteStatus({ write }) {
  if (!write || write.status === 'idle') return null;
  if (write.status === 'writing') {
    return <div className="la-status">Writing {pricesText(write.total)} to Skybox… this can take a minute.</div>;
  }
  const refused = write.failed.filter(f => f.kind === 'refused');
  const stopped = write.failed.filter(f => f.kind === 'stopped').length;
  const notSent = write.failed.filter(f => f.kind === 'not_sent').length;
  const written = write.applied.length;
  let stop = null;
  if (write.status === 'unknown') {
    // Locked until the fresh price read lands; then the table has the truth
    const text = write.locked
      ? write.message
      : 'The price write timed out; the prices were read again since: check the table before you Calculate again.';
    stop = (
      <div className="la-status warn">
        {text}
        {` (${written} written, ${write.pending.length} unconfirmed${notSent ? `, ${notSent} not sent` : ''})`}
      </div>
    );
  } else if (write.status === 'rate_limited') {
    stop = (
      <div className="la-status error">
        {write.message} {written} written, {stopped + notSent} not sent. {write.retryAt && <RetryCountdown until={write.retryAt} />}
      </div>
    );
  } else if (write.status === 'failed') {
    stop = (
      <div className="la-status error">
        {write.message} ({written} written · {stopped} in the batch that failed · {notSent} not sent)
      </div>
    );
  }
  return (
    <>
      {stop}
      {refused.length > 0 && (
        <div className="la-status error">
          {pricesText(refused.length)} refused by the server (your price limits, or a B that changed since Calculate):
          <ul className="la-detail-list">
            {refused.slice(0, MAX_LISTED).map(f => <li key={String(f.inventoryId)}>{refusedLine(f)}</li>)}
            {refused.length > MAX_LISTED && <li>…and {refused.length - MAX_LISTED} more</li>}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * @param {number} props.ranked          listings in the saved order (members or not)
 * @param {number} props.offset          the criteria's $ below the anchor comp
 * @param {number} props.step            the criteria's $ between ranks
 * @param {number} [props.floorPct]      the floor it prices at, % of each listing's cost (0 = none): the
 *   group's own, else the show's or the artist's
 * @param {string|null} [props.floorSource] where floorPct comes from ('group' | 'show' | 'artist')
 * @param {string|null} [props.performerName] the show's performer, to name the artist floor
 * @param {string|null} [props.floorChangedText] the answer was priced at another floor: why it
 *   isn't written nor continued, null when it can be
 * @param {{live: number, seats: number, price: number}|null} [props.hold] holding as these criteria
 *   set it (lionAutoHold holdOf; seats = the Live qty, 0 = off), null when off; the answer's own `hold`
 *   ({ live, seats, price, liveRanks, liveSeats }: how many ranks and seats went live) wins once it is in
 * @param {string|null} props.blockedReason  why Calculate / Continue can't run now, null when they can
 * @param {object|null} props.data       the ladder answer for exactly these criteria (continuations merged in)
 * @param {object|null} props.error      the failed Calculate / Continue for them (axios error, or LADDER_CHANGED)
 * @param {number} [props.receivedAt]    when the first answer came, for "comps as of"
 * @param {object|null} props.resume     ladderResume(data): where a stopped run picks up, null when it can't
 * @param {boolean} [props.autoContinuing] the page is chaining Continue by itself (the PARTIAL banner says so)
 * @param {object|null} [props.write]   the last price write for this group (useLadderWrite's state), null for none
 * @param {number} [props.noCompPct]     the criteria's no-comp step-down, % a day (0 = off); the answer's own
 *   noComp.pct wins once it is in
 * @param {boolean} [props.zoneFallback] the criteria's zone fallback (the step-down banner names the zone)
 * @param {object|null} [props.autoGroup] the saved group on screen (its Auto switch and no-comp clock), null
 *   for a group not saved yet
 * @param {boolean|null} [props.autoDryRun] the Auto runner is in dry run; null when unknown (a pricer)
 */
function LaLadderBar({
  ranked, offset, step, floorPct = 0, onCalculate, calculating, resume, onContinue, continuing, autoContinuing = false, blockedReason,
  data, error, receivedAt, floorSource = null, performerName = null, floorChangedText = null, hold = null, write = null,
  noCompPct = 0, zoneFallback = false, autoGroup = null, autoDryRun = null,
}) {
  const busy = calculating || continuing;
  const writing = write?.status === 'writing';
  const okCount = data ? applicableRungs(data.rungs).length : 0;
  const noCompCtx = { group: autoGroup, dryRun: autoDryRun, zoneFallback, now: Date.now() };
  const banners = data ? ladderBanners(data, !!resume, autoContinuing, noCompCtx) : [];
  const failure = error ? describeLadderError(error) : null;
  const shownOffset = data?.offset ?? offset;
  const shownStep = data?.step ?? step;
  // The answer's floor and its source when it says (its criteria are
  // these), else the floor these criteria price at
  const floor = data ? floorSummary(data) : null;
  const shownFloor = data?.floor ? data.floor.pct : floorPct;
  const shownSource = floorSourceText(data?.floor ? floor.source : floorSource, performerName);
  // Holding as the answer says (an older server sends none: the criteria's);
  // its rungs tell whether any rank is held at all (with a Live qty every
  // rank can go live)
  const shownHold = data && data.hold !== undefined ? data.hold : hold;
  const holdText = holdHeadText(shownHold, data?.rungs);
  const holdWarn = data ? holdWarning(data.rungs, shownHold?.price) : null;
  // "2nd cheapest · 1 low outlier dropped" when the low-outlier rule dropped
  // cheaper comps (an older server sends no count: nothing)
  const anchorNote = data?.anchorComp ? anchorOutlierText(data.anchorComp.outliersDropped, data.anchorComp.scope) : null;
  // "no comp: −5% a day to the floor": the criteria's step-down, or the
  // answer's own once an answer with no anchor comp says it; null when off
  const noCompHead = noCompHeadText(data?.noComp?.pct ?? noCompPct);
  return (
    <div className="la-panel">
      <div className="la-panel-head">
        <h3>Price ladder</h3>
        <span className="la-muted">
          {ranked} ranked · rank 1 {dollars(shownOffset)} below the anchor comp, then +{dollars(shownStep)} per rank (L)
          {Number(shownFloor) > 0 && ` · floor ${pctText(shownFloor)} of cost${shownSource ? ` · ${shownSource}` : ''}`}
          {holdText && ` · ${holdText}`}
          {noCompHead && ` · ${noCompHead}`}
        </span>
        <span className="la-push-right" />
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={busy || !!blockedReason}
          onClick={onCalculate}
          title={blockedReason || "Prices the group through Skybox's what-if and writes them to Skybox"}
        >
          {calculating ? 'Calculating…' : writing ? 'Writing…' : 'Calculate prices'}
        </button>
        {resume && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy || !!blockedReason}
            onClick={onContinue}
            title={blockedReason || `Price ranks ${resume.from}–${resume.to} from where the run stopped, starting +${dollars(shownStep)} above the last priced L (${dollars(resume.prevL)})`}
          >
            {continuing ? 'Continuing…' : `Continue (rungs ${resume.from}–${resume.to} of ${resume.total})`}
          </button>
        )}
      </div>

      {busy && <div className="la-status">Asking Skybox… this can take up to a minute. The prices are written once every rank has one.</div>}
      <WriteStatus write={write} />
      {floorChangedText && <div className="la-status warn">{floorChangedText} Nothing was written.</div>}
      {failure && (
        <div className={`la-status ${failure.retryable ? 'warn' : 'error'}`}>
          {failure.text}
          {failure.details?.length > 0 && (
            <ul className="la-detail-list">{failure.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
          )}
        </div>
      )}
      {data && (
        <div className="la-hint">
          {data.anchorComp && (
            <>
              {anchorCompLabel(data.anchorComp)} {listingLabel(data.anchorComp)} ×{data.anchorComp.quantity ?? '?'} at{' '}
              <strong className="la-gold">{money(data.anchorComp.unitPrice)} L</strong>
              {anchorNote && <span title={ANCHOR_OUTLIER_TITLE}> · {anchorNote}</span>} ·{' '}
            </>
          )}
          {newPricesLabel(okCount, data.noComp)} · {data.whatIfCalls ?? 0} what-if call{data.whatIfCalls === 1 ? '' : 's'} · comps as of{' '}
          <AsOf pricingAgeMs={data.pricingAgeMs} dataUpdatedAt={receivedAt} />
        </div>
      )}
      {floor && (floor.held > 0 || floor.overHold > 0 || floor.noCost > 0) && (
        <div className="la-hint">
          {floor.held > 0 && (
            <span title="Their ladder target was under the floor, so they are priced at it and the ranks above step up from there">
              {floor.held} listing{floor.held === 1 ? '' : 's'} held at {floor.held === 1 ? 'its' : 'their'} floor
            </span>
          )}
          {floor.held > 0 && floor.overHold > 0 && ' · '}
          {floor.overHold > 0 && (
            <span title="Their floor is above the hold price, so they are held at their floor instead">
              {floor.overHold} held rank{floor.overHold === 1 ? '' : 's'} at {floor.overHold === 1 ? 'its' : 'their'} floor
            </span>
          )}
          {(floor.held > 0 || floor.overHold > 0) && floor.noCost > 0 && ' · '}
          {floor.noCost > 0 && `${floor.noCost} without a cost in Skybox, so no floor`}
        </div>
      )}
      {holdWarn && <div className="la-status warn">{holdWarn}</div>}
      {banners.map(b => (
        <div key={b.code} className={`la-status ${b.kind}`}>
          {b.text}
          {b.details?.length > 0 && <ul className="la-detail-list">{b.details.map(d => <li key={d}>{d}</li>)}</ul>}
        </div>
      ))}
    </div>
  );
}

export default LaLadderBar;
